import React from "react";

// Texts of the store locator audit page. The English version is written for
// US multi-location brands (target keyword: "store locator audit", secondary
// "store locator SEO"), not translated word for word.
const content = {
  fr: {
    seo: {
      title: "Audit Store Locator gratuit - Analysez vos pages locales | LocalRanker",
      description:
        "Audit store locator gratuit : analysez le SEO local de vos pages locales. Vérifiez balises, données structurées, contenu et performance de chaque page.",
      ogTitle: "Audit Store Locator gratuit - Analysez vos pages locales | LocalRanker",
      ogDescription:
        "Audit store locator gratuit : analysez le SEO local de vos pages locales. Vérifiez balises, données structurées, contenu et performance.",
    },
    hero: {
      title: "Audit Store Locator",
      subtitle:
        "Analysez une page locale de votre store locator. Collez l'URL d'une page locale pour obtenir un audit SEO détaillé : balises, données structurées, contenu, performance.",
      cta: "Lancer mon audit",
    },
    form: {
      placeholder: "https://exemple.com/magasin/paris-11",
      submit: "Analyser",
      submitting: "Analyse...",
    },
    toasts: {
      emptyUrl: "Veuillez saisir une URL.",
      invalidUrl: "Veuillez saisir une URL valide (ex: https://example.com/paris).",
      captcha: "Veuillez résoudre le captcha pour accéder à l'audit.",
    },
    errors: {
      tooManyRequests: "Trop de requêtes effectuées, réessayez plus tard.",
      invalidResponse: "Réponse invalide du serveur. Réessayez dans quelques instants.",
      generic: "Une erreur est survenue lors de l'analyse.",
      timeout:
        "L'analyse prend plus de temps que prévu. Réessayez dans quelques instants — si le problème persiste, le site analysé est peut-être trop lent.",
      network: "Impossible de contacter le serveur. Vérifiez votre connexion et réessayez.",
      retry: "Réessayer",
    },
    loader: {
      title: "Analyse en cours",
      steps: [
        { label: "Chargement de la page", detail: "Rendu JavaScript et contenu dynamique" },
        { label: "Extraction du contenu", detail: "Balises, images, liens et données structurées" },
        { label: "Analyse SEO local", detail: "28 critères de référencement local" },
        { label: "Évaluation des performances", detail: "Score global et pondération" },
        { label: "Génération des recommandations", detail: "Analyse IA des forces et faiblesses" },
        { label: "Finalisation", detail: "Préparation du rapport détaillé" },
      ],
    },
    modal: {
      title: "Encore une petite étape",
      submit: "Lancer l'audit",
    },
    seoSection: {
      introTitle: "Pourquoi auditer votre store locator ?",
      intro: (
        <>
          Un <strong>store locator</strong> bien optimisé est un levier majeur
          de <strong>référencement local</strong>. Chaque page locale est une
          porte d'entrée pour capter du trafic qualifié sur des requêtes
          géolocalisées — pourtant, la majorité présentent des lacunes SEO qui
          freinent leur visibilité sur Google.
        </>
      ),
      cards: [
        {
          icon: "</>",
          title: "Balises title & meta",
          text: "Vérification de l'unicité et de la géolocalisation des balises pour chaque point de vente.",
        },
        {
          icon: "{}",
          title: "Données structurées",
          text: "Analyse du balisage Schema.org LocalBusiness : présence, notes agrégées (aggregateRating) et liens d'entité (sameAs).",
        },
        {
          icon: "Aa",
          title: "Contenu local unique",
          text: "Détection des pages dupliquées et évaluation de la différenciation du contenu local.",
        },
        {
          icon: "⇵",
          title: "Maillage interne",
          text: "Vérification du linking entre pages locales et du reste du site pour faciliter le crawl.",
        },
        {
          icon: "⚡",
          title: "Performance",
          text: "Analyse de la vitesse de chargement et des Core Web Vitals sur mobile.",
        },
        {
          icon: "🌎",
          title: "SEO multi-établissements",
          text: "Cohérence NAP, stratégie de mots-clés locaux et lien avec Google Business Profile.",
        },
      ],
      blocks: [
        {
          title: "Optimisation SEO des pages locales",
          text: (
            <>
              L'<strong>optimisation SEO d'un store locator</strong> passe par un
              travail méthodique sur chaque page locale. Une page performante doit
              contenir les informations essentielles de l'établissement (adresse,
              horaires, téléphone), un contenu unique adapté à la zone de
              chalandise, et un balisage structuré complet. C'est la combinaison
              de ces éléments qui permet à vos pages de se positionner sur des
              requêtes comme "magasin + ville" ou "service + quartier".
            </>
          ),
        },
        {
          title: "Développement store locator : les bonnes pratiques",
          text: (
            <>
              Le <strong>développement d'un store locator</strong> performant ne
              se limite pas à afficher une carte et une liste d'adresses. Un store
              locator pensé pour le SEO local doit intégrer dès sa conception :
              des URLs propres par ville, un rendu côté serveur (SSR) pour
              l'indexation, un sitemap dédié aux pages locales, et une
              architecture qui facilite le crawl. Notre audit identifie les écarts
              entre votre implémentation et ces bonnes pratiques.
            </>
          ),
        },
      ],
      faqTitle: "Questions fréquentes",
      faq: [
        {
          q: "Qu'est-ce qu'un audit store locator ?",
          a: "Une analyse complète des pages locales de votre site : SEO on-page, données structurées, performance technique et pertinence du contenu pour chaque point de vente.",
        },
        {
          q: "Combien de pages puis-je auditer ?",
          a: "L'outil analyse une page locale à la fois pour vous permettre d'examiner et corriger les problèmes individuellement.",
        },
        {
          q: "Mon store locator est-il bien indexé par Google ?",
          a: "L'indexation dépend du sitemap, du maillage interne, du rendu JavaScript et de la vitesse de chargement. Notre audit identifie les blocages qui empêchent Google de découvrir vos pages locales.",
        },
      ],
    },
  },
  en: {
    seo: {
      title: "Free Store Locator SEO Audit | Localranker",
      description:
        "Run a free store locator audit on any location page. Check title tags, LocalBusiness schema, NAP, local content and mobile speed to improve local SEO.",
      ogTitle: "Free Store Locator SEO Audit | Localranker",
      ogDescription:
        "Audit any store locator page for local SEO: title tags, LocalBusiness schema, NAP, local content and mobile performance.",
    },
    hero: {
      title: "Store Locator SEO Audit",
      subtitle:
        "Paste the URL of one of your location pages and get a detailed local SEO audit: title tags, structured data, local content, internal links and performance.",
      cta: "Audit my page",
    },
    form: {
      placeholder: "https://example.com/locations/austin-tx",
      submit: "Run audit",
      submitting: "Analyzing...",
    },
    toasts: {
      emptyUrl: "Please enter a URL.",
      invalidUrl: "Please enter a valid URL (e.g. https://example.com/austin).",
      captcha: "Please complete the captcha to run the audit.",
    },
    errors: {
      tooManyRequests: "Too many requests. Please try again later.",
      invalidResponse: "The server sent an invalid response. Please try again in a moment.",
      generic: "Something went wrong during the audit.",
      timeout:
        "The audit is taking longer than expected. Please try again in a moment. If it keeps happening, the page you are auditing may be too slow to load.",
      network: "We couldn't reach the server. Check your connection and try again.",
      retry: "Try again",
    },
    loader: {
      title: "Audit in progress",
      steps: [
        { label: "Loading the page", detail: "Rendering JavaScript and dynamic content" },
        { label: "Extracting content", detail: "Tags, images, links and structured data" },
        { label: "Local SEO analysis", detail: "Checking every local ranking factor" },
        { label: "Scoring", detail: "Overall score and weighting" },
        { label: "Writing recommendations", detail: "AI review of strengths and weaknesses" },
        { label: "Wrapping up", detail: "Preparing your detailed report" },
      ],
    },
    modal: {
      title: "One more step",
      submit: "Run the audit",
    },
    seoSection: {
      introTitle: "Why audit your store locator?",
      intro: (
        <>
          For a multi-location brand, the <strong>store locator</strong> is one
          of the strongest <strong>local SEO</strong> assets you own. Every
          location page can rank for &quot;near me&quot; and &quot;[service] in
          [city]&quot; searches and send ready-to-buy customers to a store. Yet
          many location pages ship with thin content, duplicate tags or missing
          schema that keep them out of Google&apos;s results.
        </>
      ),
      cards: [
        {
          icon: "</>",
          title: "Title tags & meta descriptions",
          text: "Checks that every location has unique tags that include its city or neighborhood.",
        },
        {
          icon: "{}",
          title: "Structured data",
          text: "Reviews your Schema.org LocalBusiness markup: presence, required fields, aggregateRating and sameAs links.",
        },
        {
          icon: "Aa",
          title: "Unique local content",
          text: "Flags near-duplicate pages and measures how much content is specific to each location.",
        },
        {
          icon: "⇵",
          title: "Internal linking",
          text: "Checks links between location pages and from the rest of the site so Google can crawl them all.",
        },
        {
          icon: "⚡",
          title: "Performance",
          text: "Looks at load speed and mobile Core Web Vitals.",
        },
        {
          icon: "🌎",
          title: "Multi-location SEO",
          text: "NAP consistency, local keyword targeting and the link with each Google Business Profile.",
        },
      ],
      blocks: [
        {
          title: "Store locator SEO: what a strong location page needs",
          text: (
            <>
              Good <strong>store locator SEO</strong> is built page by page. A
              location page that ranks shows the essentials (address, hours,
              phone number), adds content written for the area it serves, and
              carries complete structured data. Together, these are what let a
              page rank for searches like &quot;hardware store Austin&quot; or
              &quot;oil change near Midtown&quot;.
            </>
          ),
        },
        {
          title: "Building a store locator: best practices",
          text: (
            <>
              A <strong>store locator</strong> that drives traffic is more than a
              map and a list of addresses. Built for local SEO, it needs clean
              URLs per city or location, server-side rendering (SSR) so Google
              can index every page, a dedicated sitemap for location pages, and
              a site structure that is easy to crawl. Our store locator audit
              shows where your setup falls short of these practices.
            </>
          ),
        },
      ],
      faqTitle: "Frequently asked questions",
      faq: [
        {
          q: "What is a store locator audit?",
          a: "A review of your location pages covering on-page SEO, structured data, technical performance and how relevant the content is for each location.",
        },
        {
          q: "How many pages can I audit?",
          a: "The tool audits one location page at a time, so you can review and fix issues page by page.",
        },
        {
          q: "Is my store locator indexed by Google?",
          a: "Indexing depends on your sitemap, internal links, JavaScript rendering and page speed. The audit points out what may be stopping Google from finding your location pages.",
        },
      ],
    },
  },
};

export default content;
